import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import Progress from "@/shared/components/Progress/Progress";

import { clutch } from "@/shared/utils/number/number";
import { handleReactRef } from "@/shared/utils/react";
import { getEventCoordinates, isEventKey, KeyCode } from "@/shared/utils/dom";

import styles from "./EGWSlider.module.scss";

const preventEvent = (e) => {
  e.preventDefault();
};

export const EGWSlider = forwardRef(
  (
    {
      value,
      step = 1,
      isDraggableOnlyThumb,
      min = 0,
      max = 0,
      onChangeCommitted,
      thumbClassName,
      disabled,
      onChange,
      className,
      ...restProps
    },
    ref
  ) => {
    const refRoot = useRef(null);
    const refThumb = useRef(null);

    const getSliderBoundingClientRect = () => {
      const sliderRefCurrent = refRoot.current;

      if (sliderRefCurrent) {
        return sliderRefCurrent.getBoundingClientRect();
      }

      return undefined;
    };

    /**
     * @description Calculates slider progress for now based on coordinates from the given event.
     * @type {function(e: MouseEvent | TouchEvent ): (number)}
     */
    const getProgressWithEvent = (e) => {
      const sliderBoundingClientRect = getSliderBoundingClientRect();

      if (sliderBoundingClientRect) {
        const { x } = getEventCoordinates(e);
        let sliderRelativeCoordinate = x - sliderBoundingClientRect.left;

        if (sliderRelativeCoordinate <= 0) {
          return min;
        }

        const sliderWidth = sliderBoundingClientRect.width;

        if (sliderRelativeCoordinate >= sliderWidth) {
          return max;
        }

        const progressInPercents = Math.ceil((sliderRelativeCoordinate * 100) / sliderWidth);
        const newValue = ((max - min) * progressInPercents) / 100 + min;
        const extraPoints = newValue % step;

        return clutch(newValue - extraPoints, min, max);
      }

      return value;
    };

    const addSliderMoveListeners = () => {
      document.addEventListener("mousemove", onThumbMove);
      document.addEventListener("touchmove", onThumbMove, { passive: false, cancelable: true });

      document.addEventListener("mouseup", onMouseReleased);
      document.addEventListener("touchend", onMouseReleased);
    };

    const removeSliderMoveListeners = () => {
      document.removeEventListener("mousemove", onThumbMove);
      document.removeEventListener("touchmove", onThumbMove);

      document.removeEventListener("mouseup", onMouseReleased);
      document.removeEventListener("touchend", onMouseReleased);
    };

    const onThumbCapture = isDraggableOnlyThumb
      ? ({ nativeEvent }) => {
          if (onChangeCommitted) {
            onChangeCommitted(nativeEvent, getProgressWithEvent(nativeEvent));
          }
          addSliderMoveListeners();
        }
      : () => {};

    const onSliderCapture = isDraggableOnlyThumb
      ? () => {}
      : ({ nativeEvent }) => {
          // prev solution works wrong commiting updates on click and hold
          onChange(nativeEvent, getProgressWithEvent(nativeEvent));
          addSliderMoveListeners();
        };

    const onMouseReleased = (e) => {
      removeSliderMoveListeners();
      if (onChangeCommitted) {
        onChangeCommitted(e, getProgressWithEvent(e));
      }
    };

    const onThumbMove = (e) => {
      onChange(e, getProgressWithEvent(e));
    };

    const onThumbKeyDown = (e) => {
      const leftArrow = isEventKey(e, KeyCode.leftArrow);
      const rightArrow = isEventKey(e, KeyCode.rightArrow);
      const upArrow = isEventKey(e, KeyCode.upArrow);
      const downArrow = isEventKey(e, KeyCode.downArrow);

      if (leftArrow || downArrow) {
        const newValue = clutch(value - step, min, max);

        onChange(e, newValue);

        if (onChangeCommitted) {
          onChangeCommitted(e, newValue);
        }

        e.preventDefault();
      }

      if (rightArrow || upArrow) {
        const newValue = clutch(value + step, min, max);

        onChange(e, newValue);

        if (onChangeCommitted) {
          onChangeCommitted(e, newValue);
        }

        e.preventDefault();
      }
    };

    useEffect(() => {
      const sliderRefCurrent = refRoot.current;
      if (sliderRefCurrent) {
        sliderRefCurrent.addEventListener("mousedown", preventEvent, {
          passive: false,
          cancelable: true
        });
        sliderRefCurrent.addEventListener("touchstart", preventEvent, {
          passive: false,
          cancelable: true
        });
        sliderRefCurrent.addEventListener("touchmove", preventEvent, {
          passive: false,
          cancelable: true
        });
      }

      const thumbRefCurrent = refThumb.current;
      if (thumbRefCurrent) {
        thumbRefCurrent.addEventListener("mousedown", preventEvent, {
          passive: false,
          cancelable: true
        });
        thumbRefCurrent.addEventListener("touchstart", preventEvent, {
          passive: false,
          cancelable: true
        });
        thumbRefCurrent.addEventListener("touchmove", preventEvent, {
          passive: false,
          cancelable: true
        });
      }

      return () => {
        if (sliderRefCurrent) {
          sliderRefCurrent.removeEventListener("mousedown", preventEvent);
          sliderRefCurrent.removeEventListener("touchstart", preventEvent);
          sliderRefCurrent.removeEventListener("touchmove", preventEvent);
        }
        if (thumbRefCurrent) {
          thumbRefCurrent.removeEventListener("mousedown", preventEvent);
          thumbRefCurrent.removeEventListener("touchstart", preventEvent);
          thumbRefCurrent.removeEventListener("touchmove", preventEvent);
        }
      };
    }, []);

    const handleRef = useCallback((instance) => {
      handleReactRef(ref, instance);
      refRoot.current = instance;
    }, [ref]);

    return (
      <Progress
        showThumb

        ref={handleRef}
        className={classnames(className, styles["root"], {
          [styles["draggable-only-thumb"]]: isDraggableOnlyThumb,
        })}
        value={value}
        min={min}
        max={max}
        disabled={disabled}

        onMouseDown={onSliderCapture}
        onTouchStart={onSliderCapture}

        trackClassName={styles["track"]}

        thumbClassName={classnames(styles["thumb"], thumbClassName)}
        thumbProps={{
          ref: refThumb,
          onMouseDown: onThumbCapture,
          onTouchStart: onThumbCapture,
          onKeyDown: onThumbKeyDown,
        }}

        {...restProps}
      />
    );
  }
);

EGWSlider.defaultProps = {
  isDraggableOnlyThumb: false,
  thumbClassName : "",
};

EGWSlider.propTypes = {
  value: PropTypes.number,
  step: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  className: PropTypes.string,
  thumbClassName: PropTypes.string,
  isDraggableOnlyThumb: PropTypes.bool,
  disabled: PropTypes.bool,
  onChangeCommitted: PropTypes.func,
  onChange: PropTypes.func.isRequired
};
